// Maps a status's persisted `color` name (see order_status_master /
// LiveOrderStatusService) to the full set of Tailwind classes needed to
// render that ONE color consistently everywhere the status appears: the
// column header dot + title, the top status filter pill (active state), and
// the "move to this status" quick-action button inside a card. Every status
// gets a distinct color (enforced server-side — see order_status_master),
// so a seller can recognize a status by color alone across the whole page.
export const STATUS_COLOR_STYLES = {
  green: {
    dot: "bg-green-500",
    text: "text-green-700",
    solid: "bg-green-600 hover:bg-green-700",
    pillActive: "border-green-500 bg-green-500",
    badge: "bg-green-100 text-green-700",
  },
  blue: {
    dot: "bg-blue-500",
    text: "text-blue-700",
    solid: "bg-blue-600 hover:bg-blue-700",
    pillActive: "border-blue-500 bg-blue-500",
    badge: "bg-blue-100 text-blue-700",
  },
  purple: {
    dot: "bg-purple-500",
    text: "text-purple-700",
    solid: "bg-purple-600 hover:bg-purple-700",
    pillActive: "border-purple-500 bg-purple-500",
    badge: "bg-purple-100 text-purple-700",
  },
  indigo: {
    dot: "bg-indigo-500",
    text: "text-indigo-700",
    solid: "bg-indigo-600 hover:bg-indigo-700",
    pillActive: "border-indigo-500 bg-indigo-500",
    badge: "bg-indigo-100 text-indigo-700",
  },
  amber: {
    dot: "bg-amber-500",
    text: "text-amber-700",
    solid: "bg-amber-600 hover:bg-amber-700",
    pillActive: "border-amber-500 bg-amber-500",
    badge: "bg-amber-100 text-amber-700",
  },
  red: {
    dot: "bg-red-500",
    text: "text-red-700",
    solid: "bg-red-600 hover:bg-red-700",
    pillActive: "border-red-500 bg-red-500",
    badge: "bg-red-100 text-red-700",
  },
  teal: {
    dot: "bg-teal-500",
    text: "text-teal-700",
    solid: "bg-teal-600 hover:bg-teal-700",
    pillActive: "border-teal-500 bg-teal-500",
    badge: "bg-teal-100 text-teal-700",
  },
  pink: {
    dot: "bg-pink-500",
    text: "text-pink-700",
    solid: "bg-pink-600 hover:bg-pink-700",
    pillActive: "border-pink-500 bg-pink-500",
    badge: "bg-pink-100 text-pink-700",
  },
  slate: {
    dot: "bg-slate-400",
    text: "text-slate-700",
    solid: "bg-slate-600 hover:bg-slate-700",
    pillActive: "border-slate-500 bg-slate-500",
    badge: "bg-slate-100 text-slate-700",
  },
};

const FALLBACK_STYLE = {
  dot: "bg-gray-400",
  text: "text-gray-700",
  solid: "bg-gray-600 hover:bg-gray-700",
  pillActive: "border-gray-500 bg-gray-500",
  badge: "bg-gray-100 text-gray-700",
};

export function getStatusColorStyle(color) {
  return STATUS_COLOR_STYLES[color] || FALLBACK_STYLE;
}

// Hex equivalents of the same badge palette (bg-*-100 / text-*-700), for
// contexts that can't use Tailwind classes — e.g. the raw-HTML order PDF
// export, which renders outside the app's stylesheet.
const STATUS_HEX_COLORS = {
  green: ['#dcfce7', '#15803d'],
  blue: ['#dbeafe', '#1d4ed8'],
  purple: ['#f3e8ff', '#7e22ce'],
  indigo: ['#e0e7ff', '#4338ca'],
  amber: ['#fef3c7', '#b45309'],
  red: ['#fee2e2', '#b91c1c'],
  teal: ['#ccfbf1', '#0f766e'],
  pink: ['#fce7f3', '#be185d'],
  slate: ['#f1f5f9', '#334155'],
};

const FALLBACK_HEX_COLORS = ['#f3f4f6', '#374151'];

export function getStatusHexColors(color) {
  return STATUS_HEX_COLORS[color] || FALLBACK_HEX_COLORS;
}

// A softer bg-*-50/text-*-700/border-*-200 variant — used by the public
// order tracking page's status pill, which needs a visible border (the badge
// variant above has none). Every class here is written out literally (never
// built from a template string) since Tailwind's JIT scanner only picks up
// class names it can see as-is in source.
const STATUS_SOFT_STYLES = {
  green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
  pink: { bg: "bg-pink-50", text: "text-pink-700", border: "border-pink-200" },
  slate: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
};

const FALLBACK_SOFT_STYLE = { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" };

export function getStatusSoftStyle(color) {
  return STATUS_SOFT_STYLES[color] || FALLBACK_SOFT_STYLE;
}

// Back-compat named export — some call sites only need the dot class.
export const STATUS_DOT_CLASS = Object.fromEntries(
  Object.entries(STATUS_COLOR_STYLES).map(([color, style]) => [color, style.dot])
);
